import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PriceDisclaimer } from "@/components/PriceDisclaimer";
import { TrendingUp, TrendingDown, Minus, Search, Database, Wifi, MapPin, Clock } from "lucide-react";

const CACHE_KEY = "gc_mandi_cache_v2";
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

const trendIcon = (t) => {
  if (t === "up") return <TrendingUp size={16} className="trend-up" />;
  if (t === "down") return <TrendingDown size={16} className="trend-down" />;
  return <Minus size={16} className="trend-stable" />;
};

const Mandi = () => {
  const { t, lang } = useLang();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const [data, setData] = useState({ source: "", prices: [] });
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [q, setQ] = useState("");
  const [variety, setVariety] = useState("");

  // Load states once
  useEffect(() => {
    api.get("/prices/states").then((r) => setStates(r.data.states || []));
  }, []);

  // Load districts when state changes
  useEffect(() => {
    setSelectedDistrict("");
    if (!selectedState) {
      setDistricts([]);
      return;
    }
    api
      .get("/prices/districts", { params: { state: selectedState } })
      .then((r) => setDistricts(r.data.districts || []));
  }, [selectedState]);

  // Fetch prices with fallback caching
  useEffect(() => {
    const cacheKey = `${CACHE_KEY}:${selectedState}:${selectedDistrict}:${variety}`;
    setLoading(true);
    const params = {};
    if (selectedState) params.state = selectedState;
    if (selectedDistrict) params.district = selectedDistrict;
    if (variety) params.variety = variety;

    api
      .get("/prices", { params })
      .then((r) => {
        setData(r.data);
        localStorage.setItem(
          cacheKey,
          JSON.stringify({ ts: Date.now(), payload: r.data })
        );
      })
      .catch(() => {
        // Fallback to cached data if API is slow/down
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const { ts, payload } = JSON.parse(cached);
            if (Date.now() - ts < CACHE_TTL_MS) {
              setData({ ...payload, source: "cache" });
            }
          } catch { /* ignore */ }
        }
      })
      .finally(() => setLoading(false));
  }, [selectedState, selectedDistrict, variety]);

  const varieties = useMemo(() => {
    const s = new Set(data.prices.map((p) => p.variety).filter(Boolean));
    return Array.from(s);
  }, [data]);

  const filtered = useMemo(() => {
    if (!q) return data.prices;
    return data.prices.filter((p) => {
      const qs = q.toLowerCase();
      return (
        p.mandi_name?.toLowerCase().includes(qs) ||
        p.state?.toLowerCase().includes(qs) ||
        p.district?.toLowerCase().includes(qs)
      );
    });
  }, [data, q]);

  const sourceBadge = data.source === "data.gov.in"
    ? { text: t.mandi.source_live, cls: "bg-green-100 text-green-800", icon: Wifi }
    : data.source === "cache"
    ? { text: t.mandi.source_cache, cls: "bg-blue-100 text-blue-800", icon: Database }
    : null;
  const SrcIcon = sourceBadge?.icon;

  const fmtDate = (s) => {
    const dt = new Date(s);
    return isNaN(dt) ? s : dt.toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const agoText = () => {
    if (!data.updated_at) return "";
    const mins = Math.max(0, Math.floor((now - new Date(data.updated_at).getTime()) / 60000));
    if (mins < 1) return t.mandi.just_now;
    if (mins < 60) return `${mins} ${t.mandi.min_ago}`;
    return `${Math.floor(mins / 60)} ${t.mandi.hr_ago}`;
  };

  return (
    <div data-testid="mandi-page" className="container-gc px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900">{t.mandi.title}</h1>
          <p className="text-stone-600 mt-1">{t.mandi.subtitle}</p>
          {data.updated_at && (
            <p data-testid="mandi-last-updated" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-stone-500">
              <Clock size={13} className="text-[#1E4D2B]" /> {t.mandi.last_updated} {agoText()}
            </p>
          )}
        </div>
        {sourceBadge && (
          <div
            data-testid="data-source-badge"
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${sourceBadge.cls}`}
          >
            <SrcIcon size={14} />
            {sourceBadge.text}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card-gc p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <select
          data-testid="mandi-state-filter"
          className="input-gc"
          value={selectedState}
          onChange={(e) => setSelectedState(e.target.value)}
        >
          <option value="">{t.mandi.all_states}</option>
          {states.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          data-testid="mandi-district-filter"
          className="input-gc"
          value={selectedDistrict}
          onChange={(e) => setSelectedDistrict(e.target.value)}
          disabled={!selectedState}
        >
          <option value="">{t.mandi.all_districts}{!selectedState ? ` (${t.mandi.select_state_first})` : ""}</option>
          {districts.map((dd) => (
            <option key={dd} value={dd}>{dd}</option>
          ))}
        </select>
        <select
          data-testid="mandi-variety-filter"
          className="input-gc"
          value={variety}
          onChange={(e) => setVariety(e.target.value)}
        >
          <option value="">{t.mandi.all_varieties}</option>
          {varieties.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
        <div className="relative flex items-center">
          <Search size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            data-testid="mandi-search-input"
            className="input-gc pl-10"
            placeholder={t.mandi.search_placeholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="card-gc p-12 text-center text-stone-500">{t.common.loading}</div>
      ) : filtered.length === 0 ? (
        <div className="card-gc p-12 text-center text-stone-500">{t.mandi.no_data}</div>
      ) : (
        <>
          {/* Mobile: Cards */}
          <div className="grid sm:hidden gap-3" data-testid="mandi-cards-mobile">
            {filtered.map((p, idx) => (
              <div key={p.id || `${p.mandi_name}-${idx}`} className="card-gc p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-stone-900">{p.mandi_name}</div>
                    <div className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                      <MapPin size={11} /> {p.district ? `${p.district}, ` : ""}{p.state}
                    </div>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 font-medium">
                    {p.variety}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                  <div>
                    <div className="text-stone-500 text-xs">{t.mandi.cell_min}</div>
                    <div className="font-semibold">₹{p.min_price.toFixed(0)}</div>
                  </div>
                  <div>
                    <div className="text-stone-500 text-xs">{t.mandi.cell_modal}</div>
                    <div className="font-bold text-[#1E4D2B] text-lg">₹{p.avg_price.toFixed(0)}</div>
                  </div>
                  <div>
                    <div className="text-stone-500 text-xs">{t.mandi.cell_max}</div>
                    <div className="font-semibold">₹{p.max_price.toFixed(0)}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-stone-200 flex items-center justify-between text-sm">
                  <span data-testid={`mandi-card-updated-${idx}`} className="text-stone-500 text-xs">{t.mandi.updated_label} {fmtDate(p.entry_date)}</span>
                  <span className="flex items-center gap-1 font-medium">
                    {trendIcon(p.trend)}
                    <span className={p.trend === "up" ? "trend-up" : p.trend === "down" ? "trend-down" : "trend-stable"}>
                      {p.change_pct > 0 ? "+" : ""}{p.change_pct?.toFixed(1) ?? 0}%
                    </span>
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table */}
          <div className="hidden sm:block card-gc overflow-hidden" data-testid="mandi-table-desktop">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F1EA] text-stone-700">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">{t.mandi.col_state}</th>
                    <th className="text-left px-4 py-3 font-semibold">{t.mandi.col_district}</th>
                    <th className="text-left px-4 py-3 font-semibold">{t.mandi.col_mandi}</th>
                    <th className="text-left px-4 py-3 font-semibold">{t.mandi.col_variety}</th>
                    <th className="text-right px-4 py-3 font-semibold">{t.mandi.col_min}</th>
                    <th className="text-right px-4 py-3 font-semibold">{t.mandi.col_max}</th>
                    <th className="text-right px-4 py-3 font-semibold">{t.mandi.col_modal}</th>
                    <th className="text-right px-4 py-3 font-semibold">{t.mandi.col_arrival}</th>
                    <th className="text-right px-4 py-3 font-semibold">{t.mandi.col_trend}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, idx) => (
                    <tr
                      key={p.id || `${p.mandi_name}-${idx}`}
                      data-testid={`mandi-row-${idx}`}
                      className="border-t border-stone-200 hover:bg-stone-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-stone-700">{p.state}</td>
                      <td className="px-4 py-3 text-stone-600">{p.district || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-stone-900">{p.mandi_name}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 font-medium">
                          {p.variety}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">₹{p.min_price.toFixed(0)}</td>
                      <td className="px-4 py-3 text-right">₹{p.max_price.toFixed(0)}</td>
                      <td className="px-4 py-3 text-right font-bold text-[#1E4D2B]">₹{p.avg_price.toFixed(0)}</td>
                      <td data-testid={`mandi-row-updated-${idx}`} className="px-4 py-3 text-right text-xs text-stone-500 whitespace-nowrap">{fmtDate(p.entry_date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {trendIcon(p.trend)}
                          <span
                            className={`font-medium ${
                              p.trend === "up" ? "trend-up" : p.trend === "down" ? "trend-down" : "trend-stable"
                            }`}
                          >
                            {p.change_pct > 0 ? "+" : ""}{p.change_pct?.toFixed(1) ?? 0}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <p className="mt-4 text-xs text-stone-500 text-center">
        {t.mandi.showing_prefix} <strong>{filtered.length}</strong> {t.mandi.showing_note}
      </p>
      <PriceDisclaimer className="mt-6 mb-4" />
    </div>
  );
};

export default Mandi;
